import produce from 'immer'
import { assign, get, omit, pick, set } from 'lodash'

import { transformIngredientKeywordToInitialState } from '../../../common/graphql/transformers'

import {
  addKeywordNode,
  deleteByCheckedKeywordKeysOnKeywordTrees,
  updateCheckedKeywordsLanguageOnKeywordTrees,
} from '../../helpers'
import {
  ADD_KEYWORD,
  CHANGE_CHECKED_KEYWORDS_LANGUAGE,
  CHECK_MAIN_KEYWORD_NODES_FOR_TYPE,
  CLEAR_MAIN_KEYWORD_ERROR,
  DELETE_CHECKED_KEYWORDS,
  FETCH_INGREDIENT,
  RENAME_KEYWORD,
  RESET_INGREDIENT_KEYWORDS,
  SET_DATA_FOR_RENAMING_KEYWORD,
  UPDATE_INGREDIENT_KEYWORD_DETAILS,
} from '../action-types'

const ingredientMainKeywordsReducer = (state, action) => {
  switch (action.type) {
    case FETCH_INGREDIENT.COMPLETE:
      return produce(state, nextState => {
        const { synonyms, varieties, discarded, uncategorized } =
          transformIngredientKeywordToInitialState(
            action.payload.data.ingredient,
          )

        // eslint-disable-next-line no-param-reassign
        nextState.keywords = {}
        assign(nextState.keywords, {
          main: {
            isLoading: false,
            error: undefined,
            initialData: {
              synonyms,
              varieties,
              discarded,
              uncategorized,
            },
            data: { synonyms, varieties, discarded, uncategorized },
            checkedKeywordKeys: {
              synonyms: [],
              varieties: [],
              discarded: [],
              uncategorized: [],
            },
          },
          supporting: {
            isLoading: false,
            error: undefined,
            data: { synonyms, varieties, discarded, uncategorized },
            checkedKeywordKeys: {
              synonyms: [],
              varieties: [],
              discarded: [],
              uncategorized: [],
            },
          },
        })

        return nextState
      })

    case UPDATE_INGREDIENT_KEYWORD_DETAILS.START:
      return produce(state, nextState => {
        assign(nextState.keywords, {
          main: {
            ...nextState.keywords.main,
            isLoading: true,
            error: undefined,
          },
        })

        return nextState
      })

    case UPDATE_INGREDIENT_KEYWORD_DETAILS.COMPLETE:
      return produce(state, nextState => {
        const { synonyms, varieties, discarded, uncategorized } =
          transformIngredientKeywordToInitialState(
            action.payload.data.updateIngredientKeywordDetails,
          )

        const newState = {
          isLoading: false,
          error: undefined,
          isDirty: false,
          initialData: {
            synonyms,
            varieties,
            discarded,
            uncategorized,
          },
          data: { synonyms, varieties, discarded, uncategorized },
          checkedKeywordKeys: {
            synonyms: [],
            varieties: [],
            discarded: [],
            uncategorized: [],
          },
        }

        return assign(nextState, {
          data: {
            ...nextState.data,
            ...pick(action.payload.data.updateIngredientKeywordDetails, [
              'updatedAt',
              'updatedBy',
            ]),
          },

          keywords: {
            main: newState,
            supporting: {
              ...omit(newState, ['initialData', 'isDirty']),
              isFinishedTransfering: false,
            },
          },
        })
      })

    case UPDATE_INGREDIENT_KEYWORD_DETAILS.FAIL:
      return produce(state, nextState => {
        const { message, graphQLErrors } = action.error

        const duplicated = get(
          graphQLErrors,
          '[0].extensions.exception.duplicated',
        )

        assign(nextState.keywords, {
          main: {
            ...nextState.keywords.main,
            isLoading: false,
            error: {
              message,
              duplicated,
            },
          },
        })

        return nextState
      })

    case CLEAR_MAIN_KEYWORD_ERROR:
      return produce(state, nextState => {
        assign(nextState.keywords, {
          main: {
            ...nextState.keywords.main,
            error: { message: undefined },
          },
        })

        return nextState
      })

    case ADD_KEYWORD:
      return produce(state, nextState => {
        const { node, field } = action.payload

        const treeDataWithNewNode = addKeywordNode(
          node,
          field,
          nextState.keywords.main,
        )

        assign(nextState.keywords, {
          main: {
            ...nextState.keywords.main,
            ...treeDataWithNewNode,
            isDirty: true,
            dataForRenaming: { id: node.id },
          },
        })

        return nextState
      })

    case DELETE_CHECKED_KEYWORDS:
      return produce(state, nextState => {
        try {
          const { synonyms, varieties, uncategorized, discarded } =
            deleteByCheckedKeywordKeysOnKeywordTrees(nextState.keywords.main)

          assign(nextState.keywords, {
            main: {
              ...nextState.keywords.main,
              data: {
                synonyms,
                varieties,
                uncategorized,
                discarded,
              },
              checkedKeywordKeys: {
                synonyms: [],
                varieties: [],
                discarded: [],
                uncategorized: [],
              },
              isDirty: true,
            },
          })
        } catch (error) {
          assign(nextState.keywords, {
            main: {
              ...nextState.keywords.main,
              isLoading: false,
              error: { message: error.message },
            },
          })
        }

        return nextState
      })

    case CHECK_MAIN_KEYWORD_NODES_FOR_TYPE:
      return produce(state, nextState => {
        const { type, checkedKeys } = action.payload
        const pathToAddNodeTo = `checkedKeywordKeys.${type}`

        assign(nextState.keywords, {
          main: set(nextState.keywords.main, pathToAddNodeTo, checkedKeys),
        })

        return nextState
      })

    case CHANGE_CHECKED_KEYWORDS_LANGUAGE:
      return produce(state, nextState => {
        const languageId = action.payload

        try {
          const { synonyms, varieties, uncategorized, discarded } =
            updateCheckedKeywordsLanguageOnKeywordTrees(
              nextState.keywords.main,
              languageId,
            )

          assign(nextState.keywords, {
            main: {
              ...nextState.keywords.main,
              data: {
                synonyms,
                varieties,
                uncategorized,
                discarded,
              },
              checkedKeywordKeys: {
                synonyms: [],
                varieties: [],
                discarded: [],
                uncategorized: [],
              },
              isDirty: true,
            },
          })
        } catch (error) {
          assign(nextState.keywords, {
            main: {
              ...nextState.keywords.main,
              isLoading: false,
              error: { message: error.message },
            },
          })
        }

        return nextState
      })

    case RENAME_KEYWORD:
      return produce(state, nextState => {
        const { term, path, field } = action.payload

        assign(nextState.keywords, {
          main: {
            ...nextState.keywords.main,
            ...set(nextState.keywords.main, `data.${field}${path}`, {
              ...get(nextState.keywords.main, `data.${field}${path}`),
              term,
              isModified: true,
            }),
            isDirty: true,
          },
        })

        return nextState
      })

    case SET_DATA_FOR_RENAMING_KEYWORD:
      return produce(state, nextState => {
        assign(nextState.keywords, {
          main: {
            ...nextState.keywords.main,
            dataForRenaming: action.payload,
          },
        })

        return nextState
      })

    case RESET_INGREDIENT_KEYWORDS:
      return produce(state, nextState => {
        const { synonyms, varieties, discarded, uncategorized } =
          nextState.keywords.main.initialData

        assign(nextState.keywords, {
          main: {
            ...nextState.keywords.main,
            error: undefined,
            isDirty: false,
            data: {
              synonyms,
              varieties,
              discarded,
              uncategorized,
            },
            checkedKeywordKeys: {
              synonyms: [],
              varieties: [],
              discarded: [],
              uncategorized: [],
            },
          },
        })

        return nextState
      })

    default:
      return state
  }
}

export default ingredientMainKeywordsReducer
